import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Settings2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { errMsg } from "@/lib/api";
import {
  useClearPlatformConfigurationCache,
  usePlatformConfigurations,
  useUpsertPlatformConfiguration,
} from "@/lib/platformConfig";

const defaultJson = "{\n  \"example\": true\n}";

export default function PlatformConfiguration() {
  const configs = usePlatformConfigurations();
  const saveMutation = useUpsertPlatformConfiguration();
  const clearCacheMutation = useClearPlatformConfigurationCache();

  const [key, setKey] = useState("");
  const [jsonValue, setJsonValue] = useState(defaultJson);

  const rows = configs.data || [];

  const activeDoc = useMemo(() => {
    const normalized = key.trim().toLowerCase();
    if (!normalized) return null;
    return rows.find((doc) => doc.key === normalized) || null;
  }, [rows, key]);

  const onEdit = (doc) => {
    setKey(doc.key);
    setJsonValue(JSON.stringify(doc.value || {}, null, 2));
  };

  const onSave = (e) => {
    e.preventDefault();
    const normalized = key.trim().toLowerCase();
    if (!normalized) {
      toast.error("Configuration key is required");
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(jsonValue);
    } catch {
      toast.error("Value must be valid JSON");
      return;
    }

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      toast.error("Value must be a JSON object");
      return;
    }

    saveMutation.mutate(
      { key: normalized, value: parsed },
      {
        onSuccess: (doc) => {
          toast.success(`Saved ${doc.key} (v${doc.version})`);
          setKey(doc.key);
          setJsonValue(JSON.stringify(doc.value || {}, null, 2));
        },
        onError: (err) => toast.error(errMsg(err)),
      }
    );
  };

  const onClearCache = () => {
    const normalized = key.trim().toLowerCase();
    clearCacheMutation.mutate(
      { key: normalized || null },
      {
        onSuccess: (result) => {
          const suffix = result.scope === "key" ? ` for ${result.key}` : "";
          toast.success(`Cleared ${result.keys_cleared} cache entr${result.keys_cleared === 1 ? "y" : "ies"}${suffix}`);
        },
        onError: (err) => toast.error(errMsg(err)),
      }
    );
  };

  if (configs.isLoading) {
    return (
      <div className="p-6 lg:p-8 max-w-6xl" data-testid="platform-config-page">
        <Skeleton className="h-9 w-72 mb-4" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-6xl" data-testid="platform-config-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)] flex items-center gap-2">
          <Settings2 className="h-6 w-6 text-[var(--accent)]" /> Platform Configuration
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Manage DB-backed configuration keys used by this deployment.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-5">
        <Card className="border-[var(--subtle)] xl:col-span-3">
          <CardHeader>
            <CardTitle className="font-display">Edit Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSave} className="space-y-4">
              <div>
                <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">Key</Label>
                <Input
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  placeholder="branding"
                  className="mt-1"
                  data-testid="platform-config-key"
                />
              </div>

              <div>
                <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">Value (JSON object)</Label>
                <Textarea
                  value={jsonValue}
                  onChange={(e) => setJsonValue(e.target.value)}
                  className="mt-1 min-h-[260px] font-mono text-xs"
                  data-testid="platform-config-json"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="submit"
                  className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                  disabled={saveMutation.isPending}
                  data-testid="platform-config-save"
                >
                  {saveMutation.isPending ? "Saving..." : "Save"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClearCache}
                  disabled={clearCacheMutation.isPending}
                  data-testid="platform-config-cache-clear"
                >
                  {clearCacheMutation.isPending ? "Clearing..." : key.trim() ? "Clear key cache" : "Clear all cache"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setKey("branding");
                    setJsonValue(
                      JSON.stringify(
                        {
                          app_name: "EduOS",
                          client_name: "EduOS",
                        },
                        null,
                        2
                      )
                    );
                  }}
                >
                  Load branding template
                </Button>
              </div>

              {activeDoc && (
                <p className="text-xs text-[var(--text-muted)]">
                  Current version: v{activeDoc.version} · Last update: {activeDoc.updated_at}
                </p>
              )}
            </form>
          </CardContent>
        </Card>

        <Card className="border-[var(--subtle)] xl:col-span-2">
          <CardHeader>
            <CardTitle className="font-display">Existing Keys</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-[var(--text-muted)]">
                      No configuration keys yet.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((doc) => (
                  <TableRow key={doc.key}>
                    <TableCell className="font-mono text-xs">{doc.key}</TableCell>
                    <TableCell>v{doc.version}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(doc)}
                        data-testid={`platform-config-edit-${doc.key}`}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
